import React, { useEffect, useState } from "react";
import NavBar from "./components/navbar.tsx";
import { Outlet, useLocation } from "react-router-dom";
import classNames from "classnames";

export default function App() {
  const location = useLocation();
  const isVn = location.pathname.replace(/\/$/, "") === "/visual-novels";
  const isProductLanding = isVn || location.pathname === "/";
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    const isDark = theme === "dark";
    const themeColor = isProductLanding
      ? isDark
        ? "#070A12"
        : "#F7F8FB"
      : isDark
        ? "#1D232A"
        : "#ffffff";
    const pageTheme = isProductLanding ? "product" : "default";
    const root = document.documentElement;
    const themeMeta = getOrCreateMeta("theme-color");
    const colorSchemeMeta = getOrCreateMeta("color-scheme");

    root.dataset.pageTheme = pageTheme;
    root.dataset.colorTheme = theme;
    root.dataset.theme = theme;
    root.classList.toggle("dark", isDark);
    themeMeta.content = themeColor;
    colorSchemeMeta.content = theme;
  }, [isProductLanding, theme]);

  function toggleTheme() {
    const next = theme === "dark" ? "light" : "dark";

    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // The theme still changes for this visit when storage is unavailable.
    }

    setTheme(next);
  }

  return (
    <div
      className={classNames("font-display min-h-dvh flex flex-col transition-colors duration-300", {
        "bg-[#F7F8FB] text-slate-950 dark:bg-[#070A12] dark:text-slate-100": isProductLanding,
        "bg-base-100 text-base-content": !isProductLanding,
      })}
    >
      <a
        href="#main-content"
        className="sr-only z-[100] rounded-md bg-white px-4 py-3 text-slate-950 focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        Skip to main content
      </a>
      <NavBar theme={theme} onToggleTheme={toggleTheme} />
      <main id="main-content" className="flex-1">
        <Outlet />
      </main>
      <footer
        className={classNames(
          "footer footer-center px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] transition-colors duration-300",
          {
            "border-t border-slate-200 bg-[#F7F8FB] text-slate-500 dark:border-white/[0.08] dark:bg-[#070A12] dark:text-[#687588]":
              isProductLanding,
            "bg-base-300 text-base-content": !isProductLanding,
          },
        )}
      >
        <aside className="max-w-3xl space-y-2">
          <p>Observatory and Visual Novel Observatory are developed by Roman Khazizov.</p>
          <p>Original website content © {new Date().getFullYear()} Roman Khazizov.</p>
          <p className="text-xs leading-5">
            Third-party game artwork, screenshots, trademarks, and database content belong to their
            respective rights holders.
          </p>
        </aside>
      </footer>
    </div>
  );
}

type Theme = "light" | "dark";

const THEME_STORAGE_KEY = "observatory-theme";

function getInitialTheme(): Theme {
  const documentTheme = document.documentElement.dataset.colorTheme;

  if (documentTheme === "light" || documentTheme === "dark") {
    return documentTheme;
  }

  try {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);

    if (storedTheme === "light" || storedTheme === "dark") {
      return storedTheme;
    }
  } catch {
    // Fall through to the operating-system preference.
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getOrCreateMeta(name: string) {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);

  if (existing) {
    return existing;
  }

  const meta = document.createElement("meta");

  meta.name = name;
  document.head.appendChild(meta);

  return meta;
}
